import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Pause, Play, ChevronDown, ChevronUp, ArrowUpRight, Check } from 'lucide-react';
import { api, useAuth } from '../../context/AuthContext';

const TEAM_ROLES = ['admin', 'kanzlei_mitarbeiter', 'freelancer'];

const uhr = (s) => `${String(Math.floor(s / 3600)).padStart(2, '0')}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
const ZU = 'termin-widget-zu';

const useLaufend = (aktiv) => {
  const [nid, setNid] = useState(null);
  useEffect(() => {
    if (!aktiv) return undefined;
    let alive = true;
    const load = () => { if (!document.hidden) api.get('/kanzlei/menue-zaehler').then((r) => alive && setNid(r.data.laufender_termin || null)).catch(() => {}); };
    load();
    const t = setInterval(load, 30000);
    window.addEventListener('termin-aktualisiert', load);
    return () => { alive = false; clearInterval(t); window.removeEventListener('termin-aktualisiert', load); };
  }, [aktiv]);
  return nid;
};

const Kopf = ({ n, sek, laeuft, zu, setZu, onUhr }) => (
  <div className="flex items-center gap-2 px-3 py-2">
    <span className={`w-2 h-2 rounded-full shrink-0 ${laeuft ? 'bg-[#E0B56A] animate-pulse' : 'bg-white/40'}`} />
    <span className="min-w-0 flex-1 truncate text-[13px] font-semibold" data-testid="tw-name">{n.mandant_name || (n.mandanten_nr ? `M ${n.mandanten_nr}` : 'Gespräch ohne Mandant')}</span>
    <span className="tabular-nums text-[14px] font-semibold" data-testid="tw-uhr">{uhr(sek)}</span>
    <button type="button" onClick={onUhr} title={laeuft ? 'Pause' : 'Weiter'} className="w-7 h-7 rounded-md bg-white/10 hover:bg-white/20 inline-flex items-center justify-center transition-colors" data-testid="tw-pause">{laeuft ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}</button>
    <button type="button" onClick={() => setZu(!zu)} title={zu ? 'Aufklappen' : 'Einklappen'} className="w-7 h-7 rounded-md hover:bg-white/10 inline-flex items-center justify-center transition-colors" data-testid="tw-toggle">{zu ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</button>
  </div>
);

const Notiz = ({ nid, onDone }) => {
  const [text, setText] = useState('');
  const [status, setStatus] = useState('');
  const senden = async () => {
    if (!text.trim()) return;
    setStatus('…');
    try {
      const r = await api.get(`/kanzlei/gespraechsnotizen/${nid}`);
      const alt = (r.data.mitschrift || '').replace(/\s+$/, '');
      await api.put(`/kanzlei/gespraechsnotizen/${nid}`, { mitschrift: `${alt ? `${alt}\n\n` : ''}${text.trim()}\n` });
      setText(''); setStatus('In die Mitschrift übernommen'); onDone();
    } catch (e) { setStatus(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
  };
  return (
    <div className="px-3 pb-3 space-y-2">
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Schnellnotiz – wird an die Mitschrift angehängt" className="w-full rounded-lg bg-white/95 text-[#15202B] text-[13px] px-2.5 py-2 outline-none resize-none" data-testid="tw-notiz" />
      <div className="flex items-center gap-2">
        <button type="button" onClick={senden} disabled={!text.trim()} className="h-8 px-3 rounded-md bg-gradient-to-br from-[#E0B56A] to-[#B8893D] text-[#0B1724] text-[12px] font-semibold inline-flex items-center gap-1 disabled:opacity-50" data-testid="tw-notiz-senden"><Check className="w-3.5 h-3.5" />Übernehmen</button>
        <Link to={`/kanzlei/termin/${nid}`} className="ml-auto h-8 px-3 rounded-md border border-white/25 text-[12px] font-semibold inline-flex items-center gap-1 hover:bg-white/10 transition-colors" data-testid="tw-zurueck">Zurück zum Termin<ArrowUpRight className="w-3.5 h-3.5" /></Link>
      </div>
      {status && <p className="text-[11px] text-white/70" data-testid="tw-status">{status}</p>}
    </div>
  );
};

export const TerminWidget = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const team = !!user && TEAM_ROLES.includes(user.role);
  const nid = useLaufend(team);
  const [n, setN] = useState(null);
  const [sek, setSek] = useState(0);
  const [laeuft, setLaeuft] = useState(false);
  const [zu, setZuRaw] = useState(() => { try { return localStorage.getItem(ZU) === '1'; } catch (e) { return false; } });
  const setZu = (v) => { setZuRaw(v); try { localStorage.setItem(ZU, v ? '1' : '0'); } catch (e) { /* ignore */ } };
  const imTermin = pathname.startsWith(`/kanzlei/termin/${nid}`);
  const load = useCallback(() => { if (!nid) { setN(null); return; } api.get(`/kanzlei/gespraechsnotizen/${nid}`).then((r) => { setN(r.data); setSek(r.data.zeit_sekunden || 0); setLaeuft(!!r.data.laeuft); }).catch(() => setN(null)); }, [nid]);
  useEffect(() => { if (!imTermin) load(); }, [load, imTermin]);
  useEffect(() => { if (!laeuft) return undefined; const t = setInterval(() => setSek((s) => s + 1), 1000); return () => clearInterval(t); }, [laeuft]);
  if (!team || !nid || !n || imTermin || n.status === 'abgeschlossen') return null;
  const toggleUhr = async () => { const r = await api.post(`/kanzlei/gespraechsnotizen/${nid}/uhr`, { an: !laeuft }); setSek(r.data.zeit_sekunden); setLaeuft(r.data.laeuft); };
  return (
    <div className="fixed bottom-4 right-4 z-[40] w-[320px] max-w-[calc(100vw-2rem)] rounded-[14px] text-white bg-[#10243A]/95 backdrop-blur-md border border-white/10 shadow-[0_24px_60px_-20px_rgba(11,23,36,0.8)] motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2" data-testid="termin-widget">
      <Kopf n={n} sek={sek} laeuft={laeuft} zu={zu} setZu={setZu} onUhr={toggleUhr} />
      {!zu && <Notiz nid={nid} onDone={load} />}
    </div>
  );
};
